/** A linked reference with a delayed hover preview and an explicit keyboard/touch trigger. */
import { Info } from "lucide-react";
import { type ReactElement, type ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";

/** Keep navigation independent from preview activation, including inside clickable table rows. */
export function EntityPreview({
  label,
  children,
  preview,
}: {
  label: string;
  children: ReactNode;
  preview: (close: () => void) => ReactNode;
}): ReactElement {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const activated = useRef(false);
  const clear = (): void => clearTimeout(timer.current);
  const changeOpen = (value: boolean): void => {
    clear();
    setOpen(value);
  };
  const close = (): void => {
    activated.current = false;
    changeOpen(false);
  };
  const leave = (): void => {
    clear();
    if (!activated.current) timer.current = setTimeout(close, 200);
  };
  useEffect(() => () => clearTimeout(timer.current), []);
  return (
    <Popover
      open={open}
      onOpenChange={(value) => {
        if (value) activated.current = true;
        changeOpen(value);
      }}
    >
      <span
        className="inline-flex max-w-full items-center gap-1 align-middle"
        onClick={(event) => {
          event.stopPropagation();
          if (event.target instanceof Element && event.target.closest("a")) close();
        }}
        onKeyDown={(event) => event.stopPropagation()}
        onPointerEnter={(event) => {
          if (event.pointerType !== "mouse") return;
          clear();
          timer.current = setTimeout(() => setOpen(true), 350);
        }}
        onPointerLeave={leave}
      >
        {children}
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={t("entityPreview.preview", { name: label })}
            className="inline-flex size-7 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring pointer-coarse:size-11"
          >
            <Info className="size-3.5" aria-hidden />
          </button>
        </PopoverTrigger>
      </span>
      {open ? (
        <PopoverContent
          aria-label={t("entityPreview.preview", { name: label })}
          className="w-88 p-4 [overflow-wrap:anywhere]"
          // Reserve room while loading so collision detection does not anchor a tiny panel below the viewport.
          style={{ minHeight: "min(16rem, calc(100dvh - 1.5rem))" }}
          onPointerEnter={clear}
          onPointerLeave={leave}
          onFocusCapture={() => {
            activated.current = true;
          }}
          onOpenAutoFocus={(event) => {
            if (!activated.current) event.preventDefault();
          }}
          onCloseAutoFocus={(event) => {
            if (!activated.current) event.preventDefault();
            activated.current = false;
          }}
          onKeyDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            if (event.target instanceof Element && event.target.closest("a")) close();
          }}
        >
          {preview(close)}
        </PopoverContent>
      ) : null}
    </Popover>
  );
}
